import React, {useState, useEffect} from 'react';
import {makeRequest} from "../../utils/httpRequest";
import {formatDateString, formatDateToAmericanFormat} from "../../utils/utils";
import Config from "../../core/config";
import DatePicker from "react-datepicker";
import {useRecoilState} from "recoil";
import {loadingAtom} from "../../atoms";
import CustomerSelectAsync from "../customer/CustomerSelectAsync";
import {downloadContract, downloadDeliveryStatement, getCustomerById, getProductById, getCompanyOfCurrentUser} from "../../utils/requestUtils";

import ProductSelectAsync from "../product/ProductSelectAsync";
import {getAuthHeader} from "../../utils/auth";
import WhatsAppButton from "../../core/WhatsAppButton";
import {buildDefaultWhatsAppMessage, buildRentalSummaryMessage, openWhatsAppChat} from "../../utils/whatsappUtils";
import {saveAs} from 'file-saver';
import {useNavigate} from "react-router-dom";
import Loading from "../../core/Loading";
import {UserPermission} from "../../utils/utils";
import {usePermission} from "../../core/PermissionContext";
import eyeImg from "../../img/icons8-eye-24.png";
import ModalDiv from "../../ModalDiv";
import RentalViewer from "./RentalViewer";
import NewButton from '../common/NewButton';
import SearchButton from '../common/SearchButton';
import TableRowActions from '../common/TableRowActions';
import ListPageLayout from '../common/ListPageLayout';
import { showConfirm, showAlert } from '../../core/dialog';

const apiUrl = Config.apiUrl;
const RentalList = () => {
    const { hasUserPermission, loadingPermissions } = usePermission();
    const navigate = useNavigate();
    const searchParams = new URLSearchParams(window.location.search);
    const isBudget = searchParams.get('isBudget') === 'true';
    
    let [rentals, setRentals] = useState([]);
    const [queryString, setQueryString] = useState('');
    const [customer, setCustomer] = useState(undefined);
    const [product, setProduct] = useState(undefined);
    const [startDate, setStartDate] = useState(undefined);
    const [endDate, setEndDate] = useState(undefined);
    const [status, setStatus] = useState(0);
    const [totalValue, setTotalValue] = useState(0);
    const [shippingCost, setShippingCost] = useState(0);
    const [loading, setLoading] = useRecoilState(loadingAtom);
    const [showViewer, setShowViewer] = useState(false);
    const [selectedRentalId, setSelectedRentalId] = useState(null);


    const [pdfData, setPdfData] = useState(null);
    const [pixKey, setPixKey] = useState('');
    const [pixKeyOwnerName, setPixKeyOwnerName] = useState('');

    useEffect(() => {
        getCompanyOfCurrentUser()
            .then((company) => {
                setPixKey(company?.pixKey || '');
                setPixKeyOwnerName(company?.pixKeyOwnerName || '');
            })
            .catch(() => {});
    }, []);

    async function makeQuery(query) {
        try {
            setLoading(true);
            const newStartDate = startDate ? startDate : new Date(1900, 0, 1);
            const newEndDate = endDate ? endDate : new Date(2100, 0, 1);
            
            let url;
            if (isBudget) {
                url = `${apiUrl}/api/rentals/budgets/queryByDateAndStatus?startDate=${formatDateToAmericanFormat(newStartDate)}&endDate=${formatDateToAmericanFormat(newEndDate)}&status=${status}&customerId=${customer ? customer.id : 0}&productId=${product ? product.id : 0}`;
            } else {
                // When not in budget mode, filter out budgets from the query
                url = `${apiUrl}/api/rentals/queryByDateAndStatus?startDate=${formatDateToAmericanFormat(newStartDate)}&endDate=${formatDateToAmericanFormat(newEndDate)}&status=${status}&customerId=${customer ? customer.id : 0}&productId=${product ? product.id : 0}`;
            }
            
            const response = await makeRequest(url, 'GET');
            setRentals(response);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        makeQuery('');
    }, [isBudget]);
    //
    // useEffect(() => {
    //     makeQuery('');
    // }, [customer, product, status]);

    useEffect(() => {
        setTotalValue(rentals.reduce((acc, obj) => acc + obj.totalValue - obj.shippingCost, 0));
        setShippingCost(rentals.reduce((acc, obj) => acc + obj.shippingCost, 0));
    }, [rentals]);

    const handleStartDateChange = (date) => {
        setStartDate(date);
    };

    const handleEndDateChange = (date) => {
        setEndDate(date);
    };

    async function removeRental(id) {
        try {
            const url = apiUrl + '/api/rentals/' + id;
            await makeRequest(url, 'DELETE');
            await makeQuery(queryString);
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    }

    const handleInputChange = (e) => {
        setQueryString(e.target.value);
    };

    const handleQuery = async () => {
        await makeQuery();
    };

    const generateExcelFile = async () => {
        try {
            const newStartDate = startDate ? startDate : new Date(1900, 0, 1);
            const newEndDate = endDate ? endDate : new Date(2100, 0, 1);
            const url = `${apiUrl}/api/rentals/excelByDate?startDate=${formatDateToAmericanFormat(newStartDate)}&endDate=${formatDateToAmericanFormat(newEndDate)}`;

            const response = await fetch(url, {headers: {...getAuthHeader()}});
            if (response.ok) {
                const data = await response.blob();
                saveAs(data, `relatorio-lancamentos-${formatDateToAmericanFormat(newStartDate)}-${formatDateToAmericanFormat(newEndDate)}.xlsx`);
            } else {
                console.error('Erro ao gerar o arquivo Excel:', response.statusText);
            }
        } catch (error) {
            console.error('Erro ao fazer a solicitação para gerar o arquivo Excel:', error);
        }
    };


    async function selectCustomer(id) {
        const customer = await getCustomerById(id);
        setCustomer(customer);
    }

    const handleCustomerItemClick = async (id) => {
        await selectCustomer(id);

    };



    async function selectProduct(id) {
        const product = await getProductById(id);
        setProduct(product);
    }

    const handleProductItemClick = async (id) => {
        await selectProduct(id);
    };

    if (loadingPermissions) {
        return <Loading></Loading>;
    }
    if (isBudget ? !hasUserPermission(UserPermission.LIST_BUDGET) : !hasUserPermission(UserPermission.LIST_RENTAL)) {
        navigate('/unauthorizedaccess');
        return null;
    }

    const convertBudgetToRental = (budgetId) => {
        // Navigate to RentalManager with budget data for conversion
        navigate(`/app/rental?convertBudgetId=${budgetId}`);
    };

    const openRentalViewer = (rentalId) => {
        setSelectedRentalId(rentalId);
        setShowViewer(true);
    };

    const closeRentalViewer = () => {
        setShowViewer(false);
        setSelectedRentalId(null);
    };

    const buildWhatsAppMessage = (rental) =>
        buildDefaultWhatsAppMessage(rental.customer?.name);

    const openRentalSummaryWhatsApp = (rental) => {
        const text = buildRentalSummaryMessage(rental, pixKey, pixKeyOwnerName);
        openWhatsAppChat(rental.customer?.phone, text);
    };

    const getRentalActions = (rental) => [
        {
            key: 'resumo',
            label: 'Resumo WhatsApp',
            type: 'share',
            onClick: () => openRentalSummaryWhatsApp(rental),
        },
        {
            key: 'whatsapp',
            label: 'WhatsApp',
            visible: !!rental.customer?.phone,
            phoneNumber: rental.customer?.phone,
            personName: rental.customer?.name,
            message: buildWhatsAppMessage(rental),
            render: () => (
                <WhatsAppButton
                    phoneNumber={rental.customer?.phone}
                    personName={rental.customer?.name}
                />
            ),
        },
        {
            key: 'view',
            label: 'Visualizar',
            onClick: () => openRentalViewer(rental.id),
            render: () => (
                <img
                    src={eyeImg}
                    alt="Visualizar"
                    title="Visualizar detalhes"
                    style={{cursor: 'pointer', width: '20px', height: '20px'}}
                    onClick={() => openRentalViewer(rental.id)}
                />
            ),
        },
        {
            key: 'efetivar',
            label: 'Efetivar',
            visible: isBudget && hasUserPermission(UserPermission.CREATE_RENTAL),
            onClick: async () => {
                if (await showConfirm('Deseja converter este orçamento em aluguel?')) {
                    convertBudgetToRental(rental.id);
                }
            },
            render: () => (
                <button
                    type="button"
                    onClick={async () => {
                        if (await showConfirm('Deseja converter este orçamento em aluguel?')) {
                            convertBudgetToRental(rental.id);
                        }
                    }}
                >
                    Efetivar
                </button>
            ),
        },
        {
            key: 'contrato',
            label: 'Contrato',
            visible: !isBudget && hasUserPermission(UserPermission.DOWNLOAD_RENTAL_CONTRACT),
            onClick: () => downloadContract(rental.id, rental.code),
            render: () => (
                <button type="button" onClick={() => downloadContract(rental.id, rental.code)}>
                    Contrato
                </button>
            ),
        },
        {
            key: 'entrega',
            label: 'Entrega',
            visible: !isBudget && hasUserPermission(UserPermission.DOWNLOAD_RENTAL_DELIVERY_STATEMENT),
            onClick: () => downloadDeliveryStatement(rental.id, rental.code),
            render: () => (
                <button type="button" onClick={() => downloadDeliveryStatement(rental.id, rental.code)}>
                    Entrega
                </button>
            ),
        },
        {
            key: 'edit',
            label: 'Editar',
            type: 'edit',
            visible: hasUserPermission(UserPermission.EDIT_RENTAL),
            onClick: () => navigate(`/app/rental?id=${rental.id}${isBudget ? '&isBudget=true' : ''}`),
        },
        {
            key: 'delete',
            label: 'Excluir',
            type: 'delete',
            visible: hasUserPermission(UserPermission.REMOVE_RENTAL),
            onClick: async () => {
                if (await showConfirm(`Deseja realmente excluir este ${isBudget ? 'orçamento' : 'aluguel'}?`)) {
                    removeRental(rental.id);
                }
            },
            buttonProps: {className: 'remove-button'},
        },
    ];

    return (
        <>
            <ListPageLayout
                tableWidth={900}
                header={
                    <div style={{display: "flex", alignItems: "center", gap: '10px'}}>
                        <div className="header-label">{isBudget ? 'Consulta de Orçamentos' : 'Consulta de Aluguéis'}</div>
                        {(isBudget ? hasUserPermission(UserPermission.CREATE_BUDGET) : hasUserPermission(UserPermission.CREATE_RENTAL)) &&
                            <NewButton className={"new-button"} onClick={() => navigate(isBudget ? '/app/rental?isBudget=true' : '/app/rental')}/>
                        }
                    </div>
                }
                filters={
                    <>
            <div className="list-page-filters-row list-page-filters-row--selects" style={{display: 'flex'}}>

                <div className="list-page-filters-field" style={{width: '100%'}}>
                    <div>Cliente:</div>
                    <div className="list-page-filters-field-row" style={{marginTop: '5px'}}>
                        <CustomerSelectAsync 
                            onItemClick={handleCustomerItemClick}
                            style={{width: '100%'}}
                            placeholder="Selecione um cliente..."
                            value={customer && customer.id ? customer.id : null}
                            prefilledCustomer={customer}
                        />
                        {customer && <button className="clean-button" onClick={() => setCustomer(undefined)}>Limpar</button>}
                    </div>
                </div>

                <div className="list-page-filters-field" style={{width: '100%'}}>
                    <div>Produto:</div>
                    <div className="list-page-filters-field-row" style={{marginTop: '5px'}}>
                        <ProductSelectAsync 
                            onItemClick={handleProductItemClick}
                            style={{width: '100%'}}
                            placeholder="Selecione um produto..."
                            value={product && product.id ? product.id : null}
                            prefilledProduct={product}
                        />
                        {product && <button className="clean-button" onClick={() => setProduct(undefined)}>Limpar</button>}
                    </div>
                </div>
            </div>
            <div
                style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', width: '100%'}}>
                <div style={{marginTop: '5px', alignItems: 'center'}}>
                    <div style={{marginTop: '5px', alignItems: 'center'}}>
                        Data de Lançamento:
                        <div className="list-page-filters-toolbar-inner" style={{marginTop: '5px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px'}}>
                            <div className="list-page-filters-period" style={{display: 'flex', alignItems: 'center'}}>
                            <DatePicker
                                name={"startDate"}
                                className="datepicker"
                                selected={startDate}
                                onChange={handleStartDateChange}
                                dateFormat="dd/MM/yyyy"
                                isClearable
                                autoComplete="off"
                            />
                            <span className="list-page-filters-period-separator">até</span>
                            <DatePicker
                                name={"endDate"}
                                className="datepicker"
                                selected={endDate}
                                onChange={handleEndDateChange}
                                dateFormat="dd/MM/yyyy"
                                isClearable
                                autoComplete="off"
                            />
                            </div>
                            <div className="list-page-filters-toolbar-actions" style={{display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap'}}>
                                <select value={status} onChange={(e) => setStatus(parseInt(e.target.value))}>
                                    <option key={-1} value={-1}>Todos</option>
                                    <option key={0} value={0}>Pendente</option>
                                    <option key={1} value={1}>{isBudget ? 'Efetivado' : 'Baixado'}</option>
                                </select>
                                <SearchButton onClick={handleQuery} data-test={"search-button"}/>
                            </div>
                        </div>
                    </div>
                </div>
                {!isBudget && (
                    <div style={{marginTop: '5px', alignItems: 'center'}}>
                        <button className="excel-button" onClick={generateExcelFile}>Excel</button>
                    </div>
                )}
            </div>
                    </>
                }
                footer={
                    <>
                        <div className="total-label">Total de {isBudget ? 'Orçamentos' : 'Aluguéis'}: {rentals.length}</div>
                        <div className="total-label">Valor Total: R$ {totalValue.toLocaleString('pt-BR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                        })}</div>
                        <div className="total-label">Custo de Frete: R$ {shippingCost.toLocaleString('pt-BR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                        })}</div>
                    </>
                }
            >
            <table>
                <thead>
                <tr>
                    <th>Código</th>
                    <th>Cliente</th>
                    <th>Data de {isBudget ? 'Orçamento' : 'Aluguel'}</th>
                    <th>Valor Total</th>
                    <th/>
                </tr>
                </thead>
                <tbody>
                {rentals.map((rental) => (
                    <tr key={rental.id}>
                        <td>{rental.code}</td>
                        <td>{rental.customer?.name}</td>
                        <td>{formatDateString(rental.rentalDate)}</td>
                        <td> <div style={{textAlign: 'right'}}> R$ {rental.totalValue?.toLocaleString('pt-BR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                        })}</div></td>
                        <td>
                            <TableRowActions actions={getRentalActions(rental)} />
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>
            </ListPageLayout>

            {/* Modal para visualizar aluguel/orçamento */}
            {showViewer && (
                <ModalDiv closeModal={closeRentalViewer}>
                    <RentalViewer 
                        id={selectedRentalId} 
                        isBudget={isBudget} 
                        onClose={closeRentalViewer}
                    />
                </ModalDiv>
            )}
        </>
    );
};

export default RentalList;
